import { CommonModule } from '@angular/common';
import {
  DestroyRef,
  inject,
  SimpleChanges,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  Renderer2,
  output,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import moment from 'moment-timezone';
import type { Observable, Subscription } from 'rxjs';
import { timer } from 'rxjs';

import { TranslatePipe } from '@core/i18n-legacy';
import type { Requisition } from '@core/requisitions-types';
import { RequisitionStatus } from '@core/requisitions-types';
import { TimeAgoAsyncPipe } from '@core/time-legacy';
import {
  ButtonComponent,
  ConnectedOverlayDirective,
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  RtTooltip,
} from '@core/user-interface-legacy';

import type { RequestApprovalDisabled } from '../../../facades/single-requisition/requisitions-general.facade';

@Component({
  selector: 'rt-requisitions-toolbar',
  templateUrl: './requisitions-toolbar.component.html',
  styleUrls: ['./requisitions-toolbar.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonComponent,
    CommonModule,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownItemComponent,
    IconButtonComponent,
    IconComponent,
    OverlayOriginDirective,
    RtTooltip,
    TimeAgoAsyncPipe,
    TranslatePipe,
  ],
})
export class RequisitionsToolbarComponent {
  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly cdRef = inject(ChangeDetectorRef);

  private readonly destroyRef = inject(DestroyRef);

  public readonly isFormPristine = input<boolean>(true);
  public readonly hideManageActionsUnderMore = input<boolean>(true);
  public readonly hasManageActions = input<boolean>(undefined!);
  public readonly hasManageActionsDisabled = input<boolean>(undefined!);
  public readonly hasApproverActions = input<boolean>(undefined!);
  public readonly canCreateJob = input<boolean>(undefined!);
  public readonly hasSave = input<boolean>(true);
  public readonly requestApprovalDisabled = input<Observable<RequestApprovalDisabled>>(undefined!);

  public save = output<void>();
  public requestApproval = output<void>();
  public cancelRequest = output<void>();
  public createJob = output<void>();
  public duplicate = output<void>();
  public archive = output<void>();
  public retrieve = output<void>();
  public delete = output<void>();
  public approveRequest = output<void>();
  public rejectRequest = output<void>();

  @Input() public requisition: Requisition;

  public requestApprovalButtonDisabled: boolean;
  public requestApprovalButtonDisabledMessage: string;

  public RequisitionStatus: typeof RequisitionStatus = RequisitionStatus;
  public DropdownWidth = DropdownWidth;

  private requestApprovalDisabledSubscription: Subscription | null = null;

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.requestApprovalDisabled?.currentValue) {
      this.subscribeToRequestApprovalDisabledChanges();
    }
  }

  public ngAfterViewInit(): void {
    this.setTopOffsetForStickyPosition();
  }

  public isOlderThanHour(): boolean {
    return moment(this.requisition.updatedAt).isBefore(moment().subtract(1, 'h'));
  }

  private setTopOffsetForStickyPosition(): void {
    timer(100).subscribe(() => {
      const topOffset = this.elementRef.nativeElement.offsetParent.firstChild.offsetHeight;
      this.renderer.setStyle(this.elementRef.nativeElement, 'top', `${topOffset - 1}px`);
    });
  }

  private subscribeToRequestApprovalDisabledChanges() {
    if (this.requestApprovalDisabledSubscription) {
      this.requestApprovalDisabledSubscription.unsubscribe();
      this.requestApprovalDisabledSubscription = null;
    }

    this.requestApprovalDisabledSubscription = this.requestApprovalDisabled()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(requestApprovalDisabled => {
        this.requestApprovalButtonDisabled = !!requestApprovalDisabled;
        this.requestApprovalButtonDisabledMessage = requestApprovalDisabled
          ? requestApprovalDisabled.message
          : '';
        this.cdRef.detectChanges();
      });
  }
}
